<template>
  <div class="flex flex-col gap-6 h-full pb-6">

    <el-tabs v-model="activeTab" @tab-change="onTabChange" class="premium-tabs">

      <!-- ══ TAB 1 — Vue globale ══ -->
      <el-tab-pane name="global">
        <template #label>
          <span class="inline-flex items-center gap-2"><i class="mdi mdi-earth" aria-hidden="true"></i>Vue globale</span>
        </template>
        <!-- KPIs -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <el-card shadow="never" class="text-center">
            <div class="text-xs text-gray-500 mb-1 inline-flex items-center gap-1"><i class="mdi mdi-cash-multiple" aria-hidden="true"></i>Gains artisans</div>
            <div class="font-black text-lg text-green-600 leading-tight truncate" :title="formatFcfaFull(totalEarnings)">{{ formatFcfa(totalEarnings) }}</div>
          </el-card>
          <el-card shadow="never" class="text-center">
            <div class="text-xs text-gray-500 mb-1 inline-flex items-center gap-1"><i class="mdi mdi-bank-transfer-out" aria-hidden="true"></i>Retraits</div>
            <div class="font-black text-lg text-blue-600 leading-tight truncate" :title="formatFcfaFull(totalWithdrawals)">{{ formatFcfa(totalWithdrawals) }}</div>
          </el-card>
          <el-card shadow="never" class="text-center">
            <div class="text-xs text-gray-500 mb-1 inline-flex items-center gap-1"><i class="mdi mdi-undo-variant" aria-hidden="true"></i>Annulations</div>
            <div class="font-black text-lg text-red-500 leading-tight truncate" :title="formatFcfaFull(totalReversals)">{{ formatFcfa(totalReversals) }}</div>
          </el-card>
          <el-card shadow="never" class="text-center">
            <div class="text-xs text-gray-500 mb-1 inline-flex items-center gap-1"><i class="mdi mdi-bank" aria-hidden="true"></i>Solde net artisans</div>
            <div class="font-black text-lg leading-tight truncate" :title="formatFcfaFull(netBalance)">{{ formatFcfa(netBalance) }}</div>
          </el-card>
        </div>

        <!-- Chart -->
        <el-card shadow="never" class="mb-6">
          <template #header><span class="font-display font-bold text-profissa-text-main inline-flex items-center gap-2"><i class="mdi mdi-chart-line" aria-hidden="true"></i>Évolution mensuelle</span></template>
          <canvas ref="revenueChart" height="120"></canvas>
        </el-card>

        <!-- Transactions table -->
        <el-card shadow="never">
          <template #header>
            <div class="flex items-center justify-between flex-wrap gap-3">
              <span class="font-display font-bold text-profissa-text-main">Transactions</span>
              <div class="flex gap-3 items-center flex-wrap">
                <el-input v-model="globalSearch" placeholder="Artisan, téléphone…" clearable class="w-48">
                  <template #prefix><el-icon><Search /></el-icon></template>
                </el-input>
                <el-date-picker
                  v-model="dateRange"
                  type="daterange"
                  range-separator="→"
                  start-placeholder="Début"
                  end-placeholder="Fin"
                  value-format="YYYY-MM-DD"
                  class="!w-64"
                  @change="loadGlobal"
                />
                <el-select v-model="typeFilter" placeholder="Tous types" clearable class="w-36" @change="loadGlobal">
                  <el-option value=""           label="Tous types" />
                  <el-option value="earning"    label="Gains" />
                  <el-option value="withdrawal" label="Retraits" />
                  <el-option value="reversal"   label="Annulations" />
                </el-select>
                <el-button text @click="exportCsv(filteredGlobal, 'transactions')">
                  <i class="mdi mdi-download mr-1" aria-hidden="true"></i>CSV
                </el-button>
                <el-button @click="exportTransactions" type="success" plain size="small">Exporter CSV</el-button>
                <el-button v-if="canManageFinance" type="warning" plain size="small" @click="showAdminTransfer = true">
                  <i class="mdi mdi-cash-fast mr-1" aria-hidden="true"></i>Virement Admin
                </el-button>
                <el-button :loading="isLoading" circle @click="loadGlobal" :icon="Refresh" />
              </div>
            </div>
          </template>

          <div v-if="globalSelection.length" class="bulk-bar">
            <span class="bulk-count">{{ globalSelection.length }} sélectionné(s)</span>
            <el-button type="primary" size="small" @click="exportCsv(globalSelection, 'selection_transactions')">Exporter CSV</el-button>
          </div>

          <el-empty v-if="!isLoading && !filteredGlobal.length" description="Aucune transaction" />
          <el-table v-else v-loading="isLoading" :data="globalPaginated" stripe class="w-full" @selection-change="globalSelection = $event">
            <el-table-column type="selection" width="45" />
            <el-table-column label="Date" width="145" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
              <template #default="{ row }">
                <span class="text-xs text-profissa-text-muted whitespace-nowrap">{{ fmtDate(row.created_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Artisan" min-width="160">
              <template #default="{ row }">
                <div class="font-bold text-sm">{{ row.artisan_name || '—' }}</div>
                <div class="text-xs text-profissa-text-muted">{{ row.artisan_phone || '' }}</div>
              </template>
            </el-table-column>
            <el-table-column label="Type" width="140" sortable :sort-method="(a, b) => (a.type || '').localeCompare(b.type || '')">
              <template #default="{ row }">
                <el-tag :type="txTypeTag(row.type)">{{ typeLabel(row.type) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Montant" width="140" align="right" sortable :sort-method="(a, b) => a.amount - b.amount">
              <template #default="{ row }">
                <span class="font-bold" :class="row.type==='earning'?'text-green-500':row.type==='reversal'?'text-red-500':''">
                  {{ row.type==='earning'?'+':'-' }}{{ row.amount.toLocaleString('fr-FR') }} FCFA
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Description" min-width="180">
              <template #default="{ row }">
                <span class="text-xs text-profissa-text-muted">{{ row.description || '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Réf." width="100">
              <template #default="{ row }">
                <span class="text-[0.7rem] font-mono text-profissa-text-dim">{{ row.reference || '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="" width="60" fixed="right">
              <template #default="{ row }">
                <ActionDropdown
                  v-if="canManageFinance && row.type !== 'reversal'"
                  :actions="[{ key: 'rollback', label: 'Annuler', icon: 'RefreshLeft', danger: true }]"
                  @action="openRollback(row)"
                />
              </template>
            </el-table-column>
          </el-table>
          <div v-if="filteredGlobal.length > pageSize" class="flex justify-end mt-4">
            <el-pagination v-model:current-page="globalPage" :page-size="pageSize" :total="filteredGlobal.length" layout="prev,pager,next,total" background />
          </div>
        </el-card>
      </el-tab-pane>

      <!-- ══ TAB 2 — Wallets ══ -->
      <el-tab-pane name="wallets">
        <template #label>
          <span class="inline-flex items-center gap-2"><i class="mdi mdi-wallet-outline" aria-hidden="true"></i>Wallets</span>
        </template>
        <el-card shadow="never">
          <template #header>
            <div class="flex items-center justify-between flex-wrap gap-3">
              <span class="font-display font-bold text-profissa-text-main">Wallets artisans</span>
              <div class="flex gap-3 items-center">
                <el-input v-model="walletSearch" placeholder="Artisan, téléphone…" clearable class="w-48">
                  <template #prefix><el-icon><Search /></el-icon></template>
                </el-input>
                <el-button text @click="exportCsv(filteredWallets, 'wallets')">
                  <span class="inline-flex items-center gap-2"><i class="mdi mdi-download" aria-hidden="true"></i>CSV</span>
                </el-button>
                <el-button @click="exportWallets" type="success" plain size="small">Exporter CSV</el-button>
                <el-button :loading="isLoadingWallets" circle @click="loadWallets" :icon="Refresh" />
              </div>
            </div>
          </template>

          <div v-if="walletSelection.length" class="bulk-bar">
            <span class="bulk-count">{{ walletSelection.length }} sélectionné(s)</span>
            <el-button type="primary" size="small" @click="exportCsv(walletSelection, 'selection_wallets')">Exporter CSV</el-button>
          </div>

          <el-empty v-if="!isLoadingWallets && !filteredWallets.length" description="Aucun artisan approuvé" />
          <el-table v-else v-loading="isLoadingWallets" :data="walletsPaginated" stripe show-summary :summary-method="walletsSummary" class="w-full" @selection-change="walletSelection = $event">
            <el-table-column type="selection" width="45" />
            <el-table-column label="Artisan" min-width="160" sortable :sort-method="(a, b) => (a.artisan_name || '').localeCompare(b.artisan_name || '')">
              <template #default="{ row }">
                <div class="font-bold text-sm">{{ row.artisan_name || '—' }}</div>
                <div class="text-xs text-profissa-text-muted">{{ row.artisan_phone }}</div>
              </template>
            </el-table-column>
            <el-table-column label="Gains" align="right" width="130">
              <template #default="{ row }">
                <span class="text-green-500 font-bold">+{{ row.total_earning.toLocaleString('fr-FR') }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Retraits" align="right" width="130">
              <template #default="{ row }">
                <span class="text-profissa-text-muted">-{{ row.total_withdrawal.toLocaleString('fr-FR') }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Annulations" align="right" width="130">
              <template #default="{ row }">
                <span class="text-red-500">-{{ row.total_reversal.toLocaleString('fr-FR') }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Solde" align="right" width="150" sortable :sort-method="(a, b) => a.balance - b.balance">
              <template #default="{ row }">
                <span class="font-black text-[0.95rem]" :class="row.balance >= 0 ? 'text-green-500' : 'text-red-500'">
                  {{ row.balance.toLocaleString('fr-FR') }} FCFA
                </span>
              </template>
            </el-table-column>
            <el-table-column label="" width="60" fixed="right">
              <template #default="{ row }">
                <ActionDropdown
                  v-if="canManageFinance"
                  :actions="[{ key: 'withdraw', label: 'Créer un retrait', icon: 'Upload' }]"
                  @action="openNewWithdrawal(row)"
                />
              </template>
            </el-table-column>
          </el-table>
          <div v-if="filteredWallets.length > pageSize" class="flex justify-end mt-4">
            <el-pagination v-model:current-page="walletsPage" :page-size="pageSize" :total="filteredWallets.length" layout="prev,pager,next,total" background />
          </div>
        </el-card>
      </el-tab-pane>

      <!-- ══ TAB 3 — Retraits ══ -->
      <el-tab-pane name="withdrawals">
        <template #label>
          <span class="inline-flex items-center gap-2"><i class="mdi mdi-bank-transfer-out" aria-hidden="true"></i>Retraits</span>
        </template>
        <el-card shadow="never">
          <template #header>
            <div class="flex items-center justify-between flex-wrap gap-3">
              <span class="font-display font-bold text-profissa-text-main">Demandes de retrait</span>
              <div class="flex gap-3 items-center">
                <el-input v-model="wdSearch" placeholder="Artisan, téléphone…" clearable class="w-48">
                  <template #prefix><el-icon><Search /></el-icon></template>
                </el-input>
                <el-select v-model="wdStatusFilter" placeholder="Tous" clearable class="w-36" @change="loadWithdrawals">
                  <el-option value=""         label="Tous" />
                  <el-option value="pending"  label="En attente" />
                  <el-option value="approved" label="Approuvés" />
                  <el-option value="rejected" label="Rejetés" />
                </el-select>
                <el-button :loading="isLoadingWd" circle @click="loadWithdrawals" :icon="Refresh" />
              </div>
            </div>
          </template>

          <div v-if="wdSelection.length" class="bulk-bar">
            <span class="bulk-count">{{ wdSelection.length }} sélectionné(s)</span>
            <el-button type="primary" size="small" @click="exportCsv(wdSelection, 'selection_retraits')">Exporter CSV</el-button>
          </div>

          <el-empty v-if="!isLoadingWd && !filteredWd.length" description="Aucune demande" />
          <el-table v-else v-loading="isLoadingWd" :data="wdPaginated" stripe class="w-full" @selection-change="wdSelection = $event">
            <el-table-column type="selection" width="45" />
            <el-table-column label="Date" width="130" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
              <template #default="{ row }">
                <span class="text-xs text-profissa-text-muted whitespace-nowrap">{{ fmtDate(row.created_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Artisan" min-width="150">
              <template #default="{ row }">
                <div class="font-bold text-sm">{{ row.artisan_name || '—' }}</div>
                <div class="text-xs text-profissa-text-muted">{{ row.artisan_phone }}</div>
              </template>
            </el-table-column>
            <el-table-column label="Montant" width="140" align="right" sortable :sort-method="(a, b) => a.amount - b.amount">
              <template #default="{ row }">
                <span class="font-bold text-[0.95rem] text-profissa-accent">{{ row.amount.toLocaleString('fr-FR') }} FCFA</span>
              </template>
            </el-table-column>
            <el-table-column label="Statut" width="130" sortable :sort-method="(a, b) => (a.status || '').localeCompare(b.status || '')">
              <template #default="{ row }">
                <el-tag :type="wdStatusTagType(row.status)">{{ wdStatusLabel(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Réf. Paiement" width="150">
              <template #default="{ row }">
                <span class="text-xs font-mono text-profissa-text-muted">{{ row.ligdicash_reference || '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Notes admin" min-width="160">
              <template #default="{ row }">
                <span class="text-xs text-profissa-text-muted">{{ row.admin_notes || '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="" width="60" fixed="right">
              <template #default="{ row }">
                <ActionDropdown
                  v-if="canManageFinance && row.status === 'pending'"
                  :actions="[
                    { key: 'approve', label: 'Approuver', icon: 'Check' },
                    { key: 'reject', label: 'Rejeter', icon: 'Close', danger: true },
                  ]"
                  @action="k => k === 'approve' ? openProcessWd(row, true) : openProcessWd(row, false)"
                />
              </template>
            </el-table-column>
          </el-table>
          <div v-if="filteredWd.length > pageSize" class="flex justify-end mt-4">
            <el-pagination v-model:current-page="wdPage" :page-size="pageSize" :total="filteredWd.length" layout="prev,pager,next,total" background />
          </div>
        </el-card>
      </el-tab-pane>

      <!-- ══ TAB 4 — Par artisan ══ -->
      <el-tab-pane name="artisan">
        <template #label>
          <span class="inline-flex items-center gap-2"><i class="mdi mdi-tools" aria-hidden="true"></i>Par artisan</span>
        </template>
        <el-card shadow="never">
          <template #header>
            <div class="flex items-center justify-between flex-wrap gap-3">
              <span class="font-display font-bold text-profissa-text-main">Historique par Artisan</span>
              <el-autocomplete
                v-model="artisanSearch"
                :fetch-suggestions="fetchArtisanSuggestions"
                placeholder="Nom / téléphone…"
                clearable
                class="w-64"
                value-key="display"
                @select="selectArtisan"
                @clear="clearArtisan"
              >
                <template #prefix><el-icon><Search /></el-icon></template>
              </el-autocomplete>
            </div>
          </template>

          <div v-if="selectedArtisan" class="pb-4 mb-4 border-b border-profissa-glass-border flex items-center gap-4">
            <div class="flex-1">
              <div class="font-black text-lg">{{ selectedArtisan.full_name || formatPhone(selectedArtisan.phone) }}</div>
              <div class="text-sm text-profissa-text-muted font-mono">{{ formatPhone(selectedArtisan.phone) }}</div>
            </div>
            <div v-if="!isLoadingDetail" class="text-right mr-2 bg-white/5 px-4 py-2 rounded-xl border border-profissa-glass-border">
              <div class="text-xs uppercase tracking-widest text-profissa-text-dim font-bold mb-1">Solde net</div>
              <div class="font-black text-xl" :class="artisanNetBalance >= 0 ? 'text-green-500' : 'text-red-500'">
                {{ artisanNetBalance.toLocaleString('fr-FR') }} FCFA
              </div>
            </div>
            <el-button text circle @click="clearArtisan" :icon="Close" />
          </div>

          <template v-if="selectedArtisan">
            <el-empty v-if="!isLoadingDetail && !artisanTxRows.length" description="Aucune transaction trouvée pour cet artisan" />
            <el-table v-else v-loading="isLoadingDetail" :data="artisanTxRows" stripe class="w-full">
              <el-table-column type="selection" width="45" />
              <el-table-column label="Date" width="145" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
                <template #default="{ row }">
                  <span class="text-xs text-profissa-text-muted whitespace-nowrap">{{ fmtDate(row.created_at) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="Type" width="140">
                <template #default="{ row }">
                  <el-tag :type="txTypeTag(row.type)">{{ typeLabel(row.type) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Montant" width="140" align="right">
                <template #default="{ row }">
                  <span class="font-bold" :class="row.type==='earning'?'text-green-500':row.type==='reversal'?'text-red-500':''">
                    {{ row.type==='earning'?'+':'-' }}{{ row.amount.toLocaleString('fr-FR') }} FCFA
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Description" min-width="160">
                <template #default="{ row }">
                  <span class="text-xs text-profissa-text-muted">{{ row.description || '—' }}</span>
                </template>
              </el-table-column>
              <el-table-column label="" width="60" fixed="right">
                <template #default="{ row }">
                <ActionDropdown
                  v-if="canManageFinance && row.type !== 'reversal'"
                    :actions="[{ key: 'rollback', label: 'Annuler', icon: 'RefreshLeft', danger: true }]"
                    @action="openRollback(row)"
                  />
                </template>
              </el-table-column>
            </el-table>
          </template>
          <el-empty v-else description="Recherchez un artisan dans la barre ci-dessus" />
        </el-card>
      </el-tab-pane>

      <!-- ══ TAB 5 — Par client ══ -->
      <el-tab-pane name="client">
        <template #label>
          <span class="inline-flex items-center gap-2"><i class="mdi mdi-account-outline" aria-hidden="true"></i>Par client</span>
        </template>
        <el-card shadow="never">
          <template #header>
            <div class="flex items-center justify-between flex-wrap gap-3">
              <span class="font-display font-bold text-profissa-text-main">Historique par Client</span>
              <el-autocomplete
                v-model="clientSearch"
                :fetch-suggestions="fetchClientSuggestions"
                placeholder="Nom / téléphone…"
                clearable
                class="w-64"
                value-key="display"
                @select="selectClient"
                @clear="clearClient"
              >
                <template #prefix><el-icon><Search /></el-icon></template>
              </el-autocomplete>
            </div>
          </template>

          <div v-if="selectedClient" class="pb-4 mb-4 border-b border-profissa-glass-border flex items-center gap-4">
            <div class="flex-1">
              <div class="font-black text-lg">{{ selectedClient.full_name || formatPhone(selectedClient.phone) }}</div>
              <div class="text-sm text-profissa-text-muted font-mono">{{ formatPhone(selectedClient.phone) }}</div>
            </div>
            <div v-if="!isLoadingDetail" class="text-right mr-2 bg-white/5 px-4 py-2 rounded-xl border border-profissa-glass-border">
              <div class="text-xs uppercase tracking-widest text-profissa-text-dim font-bold mb-1">Total Payé (Libéré)</div>
              <div class="font-black text-xl text-profissa-text-main">{{ clientTotalPaid.toLocaleString('fr-FR') }} FCFA</div>
            </div>
            <el-button text circle @click="clearClient" :icon="Close" />
          </div>

          <template v-if="selectedClient">
            <el-empty v-if="!isLoadingDetail && !clientEscrowRows.length" description="Aucune transaction d'escrow trouvée" />
            <el-table v-else v-loading="isLoadingDetail" :data="clientEscrowRows" stripe class="w-full">
              <el-table-column type="selection" width="45" />
              <el-table-column label="Date" width="140" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
                <template #default="{ row }">
                  <span class="text-xs text-profissa-text-muted whitespace-nowrap">{{ fmtDate(row.created_at) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="Type" width="140">
                <template #default="{ row }">
                  <el-tag :type="row.type === 'diagnostic' ? 'info' : 'primary'">
                    {{ row.type === 'diagnostic' ? 'Diagnostic' : 'Travaux' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Montant" width="140" align="right" sortable :sort-method="(a, b) => a.amount - b.amount">
                <template #default="{ row }">
                  <span class="font-bold">{{ row.amount.toLocaleString('fr-FR') }} FCFA</span>
                </template>
              </el-table-column>
              <el-table-column label="Statut" width="140" align="center">
                <template #default="{ row }">
                  <el-tag :type="escrowTagType(row.status)">{{ escrowStatusLabel(row.status) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="N° Mission" min-width="120">
                <template #default="{ row }">
                  <span class="text-xs font-mono text-profissa-text-dim bg-white/5 px-2 py-1 rounded">{{ row.intervention_id?.slice(0,8) }}…</span>
                </template>
              </el-table-column>
            </el-table>
          </template>
          <el-empty v-else description="Recherchez un client dans la barre ci-dessus" />
        </el-card>
      </el-tab-pane>

      <!-- ══ TAB 6 — Réconciliation ══ -->
      <el-tab-pane name="reconciliation">
        <template #label>
          <span class="inline-flex items-center gap-2"><i class="mdi mdi-scale-balance" aria-hidden="true"></i>Réconciliation</span>
        </template>
        <el-card shadow="never">
          <template #header>
            <div class="flex items-center justify-between">
              <span class="font-display font-bold text-profissa-text-main">Audit de Réconciliation Financière</span>
              <el-button type="primary" :loading="isLoadingRecon" @click="loadReconciliation" :icon="Refresh">
                Lancer l'audit
              </el-button>
            </div>
          </template>

          <el-empty v-if="!isLoadingRecon && !recon" description="Aucun audit récent. Cliquez sur Lancer l'audit." />
          <div v-loading="isLoadingRecon" v-else-if="recon" class="flex flex-col gap-4">
            <el-alert
              :title="recon.status === 'ok' ? 'Comptabilité certifiée équilibrée' : 'Alerte : écart de trésorerie détecté'"
              :description="recon.status === 'ok' ? 'Le total des fonds libérés depuis l\'Escrow correspond parfaitement aux gains nets enregistrés.' : `Différence inexpliquée de ${Math.abs(recon.discrepancy).toLocaleString('fr-FR')} FCFA entre les fonds Escrow et les transactions.`"
              :type="recon.status === 'ok' ? 'success' : 'error'"
              :closable="false"
              show-icon
            />
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="bg-white/5 border border-profissa-glass-border rounded-xl p-5">
                <div class="text-sm font-black uppercase tracking-widest text-profissa-text-muted mb-4 pb-2 border-b border-profissa-glass-border inline-flex items-center gap-2">
                  <i class="mdi mdi-lock-outline" aria-hidden="true"></i>
                  <span>Balance Escrow</span>
                </div>
                <div v-for="row in escrowRows" :key="row.label" class="flex justify-between py-2 border-b border-white/5 last:border-none">
                  <span class="text-sm text-profissa-text-muted">{{ row.label }}</span>
                  <span class="font-bold text-sm" :style="{ color: row.color }">{{ row.value.toLocaleString('fr-FR') }} FCFA</span>
                </div>
                <div class="flex justify-between pt-3 mt-1 border-t border-profissa-glass-border">
                  <span class="text-sm font-bold">Reste Net (Attendu artisans)</span>
                  <span class="font-black text-green-500">{{ recon.expected_artisan_net.toLocaleString('fr-FR') }} FCFA</span>
                </div>
              </div>
              
              <div class="bg-white/5 border border-profissa-glass-border rounded-xl p-5">
                <div class="text-sm font-black uppercase tracking-widest text-profissa-text-muted mb-4 pb-2 border-b border-profissa-glass-border inline-flex items-center gap-2">
                  <i class="mdi mdi-card-account-details-outline" aria-hidden="true"></i>
                  <span>Registre Transactions</span>
                </div>
                <div v-for="row in txRows" :key="row.label" class="flex justify-between py-2 border-b border-white/5 last:border-none">
                  <span class="text-sm text-profissa-text-muted">{{ row.label }}</span>
                  <span class="font-bold text-sm" :style="{ color: row.color }">{{ row.value.toLocaleString('fr-FR') }} FCFA</span>
                </div>
                <div class="flex justify-between pt-3 mt-1 border-t border-profissa-glass-border">
                  <span class="text-sm font-bold">Solde Net Distribué</span>
                  <span class="font-black" :class="recon.artisan_net_balance >= 0 ? 'text-green-500' : 'text-red-500'">{{ recon.artisan_net_balance.toLocaleString('fr-FR') }} FCFA</span>
                </div>
              </div>
            </div>
            
            <div class="grid grid-cols-2 gap-4 mt-2">
              <el-card shadow="never" class="text-center bg-profissa-accent/5 border-profissa-accent/20">
                <el-statistic title="Bénéfice ProFissa (Commission 15%)" :value="recon.platform_commission" suffix=" FCFA">
                  <template #title><span class="text-profissa-accent font-bold">Bénéfice ProFissa (Commission 15%)</span></template>
                </el-statistic>
              </el-card>
              <el-card shadow="never" class="text-center" :class="recon.discrepancy !== 0 ? 'bg-red-500/10 border-red-500/20' : 'bg-green-500/5 border-green-500/20'">
                <el-statistic
                  :title="'Écart (Attendu − Distribué)'"
                  :value="recon.discrepancy"
                  suffix=" FCFA"
                  :value-style="{ color: recon.discrepancy !== 0 ? 'var(--el-color-danger)' : 'var(--el-color-success)', fontWeight: '900' }"
                />
              </el-card>
            </div>
          </div>
        </el-card>
      </el-tab-pane>

    </el-tabs>

    <!-- ── Dialog rollback ── -->
    <el-dialog append-to-body v-model="showRollback" title="Annuler une transaction" width="440px" custom-class="glass-dialog">
      <div v-if="rollbackTarget" class="mb-4 text-sm text-profissa-text-muted bg-white/5 p-3 rounded-lg border border-profissa-glass-border">
        <strong class="text-profissa-text-main">{{ typeLabel(rollbackTarget.type) }}</strong> de 
        <span class="font-bold text-profissa-accent">{{ rollbackTarget.amount.toLocaleString('fr-FR') }} FCFA</span>
        <div v-if="rollbackTarget.artisan_name" class="mt-1">Cible : {{ rollbackTarget.artisan_name }}</div>
      </div>
      <el-input v-model="rollbackReason" type="textarea" :rows="3" placeholder="Motif de l'annulation (Requis pour l'audit)..." />
      <template #footer>
        <div class="flex justify-end gap-2">
          <el-button @click="showRollback = false; rollbackReason = ''">Fermer</el-button>
          <el-button type="danger" :loading="isRollingBack" :disabled="!rollbackReason.trim()" @click="confirmRollback">
            Confirmer l'annulation
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- ── Dialog nouveau retrait ── -->
    <el-dialog append-to-body v-model="showNewWd" title="Créer une demande de retrait" width="440px" custom-class="glass-dialog">
      <div v-if="newWdTarget" class="mb-4 text-sm text-profissa-text-muted bg-white/5 p-3 rounded-lg border border-profissa-glass-border">
        Artisan : <strong class="text-profissa-text-main">{{ newWdTarget.artisan_name || newWdTarget.artisan_phone }}</strong><br>
        Solde disponible : <strong class="text-green-500">{{ newWdTarget.balance?.toLocaleString('fr-FR') }} FCFA</strong>
      </div>
      <label class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider mb-2 block">Montant à retirer (FCFA)</label>
      <el-input-number v-model="newWdAmount" :min="1" class="w-full" size="large" />
      <template #footer>
        <div class="flex justify-end gap-2">
          <el-button @click="showNewWd = false; newWdAmount = 0">Annuler</el-button>
          <el-button type="primary" :loading="isCreatingWd" :disabled="!newWdAmount || newWdAmount <= 0" @click="confirmNewWithdrawal">
            Valider la demande
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- ── Dialog virement admin ── -->
    <el-dialog append-to-body v-model="showAdminTransfer" title="Virement Admin vers Mobile Money" width="460px" custom-class="glass-dialog">
      <el-alert type="warning" show-icon :closable="false" class="mb-4" title="Ce virement débite directement le compte LigdiCash ProFissa. Action irréversible." />
      <div class="flex flex-col gap-4">
        <div>
          <label class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider mb-1 block">Numéro Mobile Money</label>
          <el-input v-model="adminTransferPhone" placeholder="ex: 07XXXXXXXX" clearable />
        </div>
        <div>
          <label class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider mb-1 block">Montant (FCFA)</label>
          <el-input-number v-model="adminTransferAmount" :min="1" class="w-full" size="large" />
        </div>
        <div>
          <label class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider mb-1 block">Description</label>
          <el-input v-model="adminTransferDesc" placeholder="Motif du virement..." />
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <el-button @click="showAdminTransfer = false; adminTransferPhone = ''; adminTransferAmount = 0">Annuler</el-button>
          <el-button
            type="warning"
            :loading="isAdminTransferring"
            :disabled="!adminTransferPhone.trim() || !adminTransferAmount"
            @click="confirmAdminTransfer"
          >Confirmer le virement</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- ── Dialog process retrait ── -->
    <el-dialog append-to-body v-model="showProcessWd" :title="processWdApprove ? 'Validation du Retrait' : 'Rejet du Retrait'" width="460px" custom-class="glass-dialog">
      <div v-if="processWdTarget" class="mb-4 text-sm text-profissa-text-muted bg-white/5 p-3 rounded-lg border border-profissa-glass-border">
        Bénéficiaire : <strong class="text-profissa-text-main">{{ processWdTarget.artisan_name }}</strong><br>
        Montant : <strong class="text-profissa-accent text-lg">{{ processWdTarget.amount.toLocaleString('fr-FR') }} FCFA</strong>
      </div>
      
      <div v-if="processWdApprove" class="mb-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-green-500/5 border border-green-500/20 text-sm text-profissa-text-muted">
        <i class="mdi mdi-lightning-bolt text-green-400 mt-0.5" aria-hidden="true"></i>
        <span>Le virement sera déclenché automatiquement via <strong class="text-profissa-text-main">LigdiCash</strong> sur le téléphone de l'artisan.</span>
      </div>
      
      <div>
        <label class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider mb-1 block">Notes internes (Optionnel)</label>
        <el-input v-model="processWdNotes" type="textarea" :rows="2" placeholder="Ajouter une remarque..." />
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <el-button @click="showProcessWd = false">Annuler</el-button>
          <el-button
            :type="processWdApprove ? 'success' : 'danger'"
            :loading="processingWd === processWdTarget?.id"
            @click="confirmProcessWd"
          >{{ processWdApprove ? 'Confirmer le virement' : 'Rejeter la demande' }}</el-button>
        </div>
      </template>
    </el-dialog>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { Chart, registerables } from 'chart.js'
import { ElMessage } from 'element-plus'
import { Search, Refresh, Close } from '@element-plus/icons-vue'
import ActionDropdown from './shared/ActionDropdown.vue'
import { formatFcfa, formatFcfaFull, formatPhone } from '../utils/format'
import {
  listTransactions, getArtisanTransactions, getClientEscrowHistory,
  rollbackTransaction, searchArtisans, listClients,
  listWithdrawalRequests, createWithdrawalRequest, processWithdrawalRequest,
  getArtisanWallets, getFinanceReconciliation, adminTransfer,
} from '../services/adminApi'
import { extractApiError } from '../api'
import { useAdminPermissions } from '../composables/useAdminPermissions'

Chart.register(...registerables)
const route = useRoute()
const { hasPermission } = useAdminPermissions()
const canManageFinance = computed(() => hasPermission('finance.manage.any'))

const globalSelection = ref([])
const walletSelection = ref([])
const wdSelection     = ref([])
const activeTab  = ref('global')
const pageSize   = 15

// ── Global ──
const transactions  = ref([])
const isLoading     = ref(false)
const typeFilter    = ref('')
const dateRange     = ref(null)
const globalSearch  = ref('')
const globalPage    = ref(1)

// ── Wallets ──
const wallets          = ref([])
const isLoadingWallets = ref(false)
const walletSearch     = ref('')
const walletsPage      = ref(1)

// ── Withdrawals ──
const withdrawalRequests = ref([])
const isLoadingWd        = ref(false)
const wdStatusFilter     = ref('')
const wdSearch           = ref('')
const wdPage             = ref(1)
const processingWd       = ref(null)
const showProcessWd      = ref(false)
const processWdTarget    = ref(null)
const processWdApprove   = ref(true)
const processWdNotes     = ref('')
const showNewWd          = ref(false)
const newWdTarget        = ref(null)
const newWdAmount        = ref(0)
const isCreatingWd       = ref(false)

// ── Per-artisan ──
const artisanSearch   = ref('')
const selectedArtisan = ref(null)
const artisanTxRows   = ref([])
const isLoadingDetail = ref(false)

// ── Per-client ──
const clientSearch     = ref('')
const selectedClient   = ref(null)
const clientEscrowRows = ref([])

// ── Reconciliation ──
const recon          = ref(null)
const isLoadingRecon = ref(false)

// ── Admin transfer ──
const showAdminTransfer    = ref(false)
const adminTransferPhone   = ref('')
const adminTransferAmount  = ref(0)
const adminTransferDesc    = ref('Virement admin ProFissa')
const isAdminTransferring  = ref(false)

// ── Rollback ──
const showRollback   = ref(false)
const rollbackTarget = ref(null)
const rollbackReason = ref('')
const isRollingBack  = ref(false)

// ── Chart ──
const revenueChart = ref(null)
let chartInstance  = null

// ── Helpers ──
const fmtDate   = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'
const typeLabel = (t) => ({ earning:'GAIN', withdrawal:'RETRAIT', reversal:'ANNULATION' }[t] || t)
const txTypeTag = (t) => ({ earning:'success', withdrawal:'primary', reversal:'danger' }[t] || 'info')

const wdStatusLabel   = (s) => ({ pending:'En attente', approved:'Approuvé', rejected:'Rejeté' }[s] || s)
const wdStatusTagType = (s) => ({ pending:'warning', approved:'success', rejected:'danger' }[s] || 'info')
const escrowStatusLabel = (s) => ({ held:'Séquestré', released:'Libéré', refunded:'Remboursé' }[s] || s)
const escrowTagType     = (s) => ({ held:'warning', released:'success', refunded:'info' }[s] || 'info')

// ── Computed ──
const filteredGlobal = computed(() => {
  let rows = transactions.value
  if (typeFilter.value) rows = rows.filter(t => t.type === typeFilter.value)
  if (globalSearch.value) {
    const q = globalSearch.value.toLowerCase()
    rows = rows.filter(r =>
      (r.artisan_name || '').toLowerCase().includes(q) ||
      (r.artisan_phone || '').includes(globalSearch.value)
    )
  }
  return rows
})
const globalPaginated = computed(() => {
  const start = (globalPage.value - 1) * pageSize
  return filteredGlobal.value.slice(start, start + pageSize)
})

const totalEarnings    = computed(() => transactions.value.filter(t => t.type === 'earning').reduce((s,t)=>s+t.amount,0))
const totalWithdrawals = computed(() => transactions.value.filter(t => t.type === 'withdrawal').reduce((s,t)=>s+t.amount,0))
const totalReversals   = computed(() => transactions.value.filter(t => t.type === 'reversal').reduce((s,t)=>s+t.amount,0))
const netBalance       = computed(() => totalEarnings.value - totalWithdrawals.value - totalReversals.value)

const filteredWallets = computed(() => {
  if (!walletSearch.value) return wallets.value
  const q = walletSearch.value.toLowerCase()
  return wallets.value.filter(w =>
    (w.artisan_name || '').toLowerCase().includes(q) ||
    (w.artisan_phone || '').includes(walletSearch.value)
  )
})
const walletsPaginated = computed(() => {
  const start = (walletsPage.value - 1) * pageSize
  return filteredWallets.value.slice(start, start + pageSize)
})

const filteredWd = computed(() => {
  if (!wdSearch.value) return withdrawalRequests.value
  const q = wdSearch.value.toLowerCase()
  return withdrawalRequests.value.filter(r =>
    (r.artisan_name || '').toLowerCase().includes(q) ||
    (r.artisan_phone || '').includes(wdSearch.value)
  )
})
const wdPaginated = computed(() => {
  const start = (wdPage.value - 1) * pageSize
  return filteredWd.value.slice(start, start + pageSize)
})

const artisanNetBalance = computed(() => {
  const e = artisanTxRows.value.filter(t => t.type === 'earning').reduce((s,t)=>s+t.amount,0)
  const w = artisanTxRows.value.filter(t => t.type === 'withdrawal').reduce((s,t)=>s+t.amount,0)
  const r = artisanTxRows.value.filter(t => t.type === 'reversal').reduce((s,t)=>s+t.amount,0)
  return e - w - r
})
const clientTotalPaid = computed(() =>
  clientEscrowRows.value.filter(h => h.status === 'released').reduce((s,h)=>s+h.amount,0)
)
const escrowRows = computed(() => recon.value ? [
  { label: 'Séquestre bloqué', value: recon.value.escrow_held,     color: 'var(--el-color-warning)' },
  { label: 'Fonds libérés',    value: recon.value.escrow_released, color: 'var(--el-color-success)' },
  { label: 'Fonds remboursés', value: recon.value.escrow_refunded, color: 'var(--el-text-color-secondary)' },
] : [])
const txRows = computed(() => recon.value ? [
  { label: 'Total gains bruts', value: recon.value.total_earning,    color: 'var(--el-color-success)' },
  { label: 'Total retraits',    value: recon.value.total_withdrawal, color: 'var(--el-text-color-secondary)' },
  { label: 'Total annulations', value: recon.value.total_reversal,   color: 'var(--el-color-danger)' },
] : [])

// ── Wallets summary row ──
const walletsSummary = () => {
  const all = wallets.value
  return [
    'Total Global',
    '+' + all.reduce((s,w)=>s+w.total_earning,0).toLocaleString('fr-FR'),
    '-' + all.reduce((s,w)=>s+w.total_withdrawal,0).toLocaleString('fr-FR'),
    '-' + all.reduce((s,w)=>s+w.total_reversal,0).toLocaleString('fr-FR'),
    all.reduce((s,w)=>s+w.balance,0).toLocaleString('fr-FR') + ' FCFA',
    '',
  ]
}

// ── Chart.js ──
const buildChart = async (txs) => {
  await nextTick()
  if (!revenueChart.value) return
  if (chartInstance) { chartInstance.destroy(); chartInstance = null }
  const gridColor = getComputedStyle(document.documentElement).getPropertyValue('--grid-line').trim()
  const byMonth = {}
  txs.filter(t => t.type === 'earning').forEach(t => {
    const key = t.created_at?.slice(0,7) || 'N/A'
    byMonth[key] = (byMonth[key] || 0) + t.amount
  })
  const labels = Object.keys(byMonth).sort()
  chartInstance = new Chart(revenueChart.value, {
    type: 'bar',
    data: { labels, datasets: [{ label: 'Gains générés (FCFA)', data: labels.map(k=>byMonth[k]), backgroundColor: '#FF6B00', borderRadius: 4 }] },
    options: { 
      responsive: true, 
      plugins: { legend: { display: false } }, 
      scales: { 
        y: { beginAtZero: true, grid: { color: gridColor }, ticks: { color: '#64748B' } },
        x: { grid: { display: false }, ticks: { color: '#64748B' } }
      } 
    },
  })
}

// ── Loaders ──
const loadGlobal = async () => {
  isLoading.value = true
  try {
    const from = dateRange.value?.[0] || null
    const to   = dateRange.value?.[1] ? dateRange.value[1] + 'T23:59:59' : null
    transactions.value = await listTransactions(from, to)
    buildChart(transactions.value)
    globalPage.value = 1
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

const loadWallets = async () => {
  isLoadingWallets.value = true
  try { wallets.value = await getArtisanWallets() }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoadingWallets.value = false }
}

const loadWithdrawals = async () => {
  isLoadingWd.value = true
  try { withdrawalRequests.value = await listWithdrawalRequests(wdStatusFilter.value) }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoadingWd.value = false }
}

const loadReconciliation = async () => {
  isLoadingRecon.value = true
  try { recon.value = await getFinanceReconciliation() }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoadingRecon.value = false }
}

const onTabChange = (name) => {
  if (name === 'wallets' && !wallets.value.length) loadWallets()
  if (name === 'withdrawals' && !withdrawalRequests.value.length) loadWithdrawals()
  if (name === 'reconciliation' && !recon.value) loadReconciliation()
}

// ── Artisan autocomplete ──
const fetchArtisanSuggestions = async (query, cb) => {
  if (!query.trim()) { cb([]); return }
  try {
    const res = await searchArtisans(query.trim(), '')
    cb(res.map(a => ({ ...a, display: a.full_name || a.phone || '' })))
  } catch { cb([]) }
}
const selectArtisan = async (a) => {
  selectedArtisan.value = a; isLoadingDetail.value = true
  try { artisanTxRows.value = await getArtisanTransactions(a.id) }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoadingDetail.value = false }
}
const clearArtisan = () => { selectedArtisan.value = null; artisanTxRows.value = []; artisanSearch.value = '' }

// ── Client autocomplete ──
const fetchClientSuggestions = async (query, cb) => {
  if (!query.trim()) { cb([]); return }
  try {
    const res = await listClients(query.trim())
    cb(res.map(c => ({ ...c, display: c.full_name || c.phone || '' })))
  } catch { cb([]) }
}
const selectClient = async (c) => {
  selectedClient.value = c; isLoadingDetail.value = true
  try { clientEscrowRows.value = await getClientEscrowHistory(c.id) }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoadingDetail.value = false }
}
const clearClient = () => { selectedClient.value = null; clientEscrowRows.value = []; clientSearch.value = '' }

// ── Withdrawal actions ──
const openNewWithdrawal = (w) => {
  if (!canManageFinance.value) return
  newWdTarget.value = w; newWdAmount.value = Math.max(0, w.balance); showNewWd.value = true
}
const confirmNewWithdrawal = async () => {
  if (!canManageFinance.value) return
  if (!newWdTarget.value || !newWdAmount.value) return
  isCreatingWd.value = true
  try {
    await createWithdrawalRequest(newWdTarget.value.artisan_id, newWdAmount.value)
    ElMessage.success('Demande de retrait générée avec succès.')
    showNewWd.value = false; newWdAmount.value = 0
    await loadWithdrawals()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isCreatingWd.value = false }
}
const openProcessWd = (r, approve) => {
  if (!canManageFinance.value) return
  processWdTarget.value = r; processWdApprove.value = approve
  processWdNotes.value = ''
  showProcessWd.value = true
}
const confirmProcessWd = async () => {
  if (!canManageFinance.value) return
  if (!processWdTarget.value) return
  processingWd.value = processWdTarget.value.id
  try {
    const res = await processWithdrawalRequest(processWdTarget.value.id, processWdApprove.value, processWdNotes.value)
    if (processWdApprove.value) {
      const ref = res.ligdicash_reference
      ElMessage.success(`Virement LigdiCash déclenché${ref ? ` (réf. ${ref})` : ''}.`)
    } else {
      ElMessage.success('Demande rejetée.')
    }
    showProcessWd.value = false
    await loadWithdrawals()
    if (wallets.value.length) await loadWallets()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { processingWd.value = null }
}

// ── Rollback ──
const openRollback = (tx) => {
  if (!canManageFinance.value) return
  rollbackTarget.value = tx; rollbackReason.value = ''; showRollback.value = true
}
const confirmRollback = async () => {
  if (!canManageFinance.value) return
  if (!rollbackTarget.value || !rollbackReason.value.trim()) return
  isRollingBack.value = true
  try {
    const res = await rollbackTransaction(rollbackTarget.value.id, rollbackReason.value.trim())
    ElMessage.success(`Opération annulée (Réf. interne: ${res.reversal_id.slice(0,8)})`)
    showRollback.value = false; rollbackReason.value = ''
    if (activeTab.value === 'global') await loadGlobal()
    else if (activeTab.value === 'artisan' && selectedArtisan.value) await selectArtisan(selectedArtisan.value)
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isRollingBack.value = false }
}

// ── CSV export ──
const exportCsv = (rows, filename) => {
  if (!rows.length) return
  const keys = Object.keys(rows[0])
  const lines = [keys.join(','), ...rows.map(r => keys.map(k => JSON.stringify(r[k] ?? '')).join(','))]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `ProFissa_${filename}_${new Date().toISOString().slice(0,10)}.csv`
  a.click(); URL.revokeObjectURL(a.href)
}

const exportCsvColumns = (data, filename, columns) => {
  if (!data.length) { ElMessage.warning('Aucune donnée à exporter.'); return }
  const header = columns.map(c => c.label).join(';')
  const rows = data.map(row => columns.map(c => {
    const val = c.key.split('.').reduce((o, k) => o?.[k], row) ?? ''
    return `"${String(val).replace(/"/g, '""')}"`
  }).join(';'))
  const csv = [header, ...rows].join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

const exportTransactions = () => exportCsvColumns(
  transactions.value,
  `transactions_${new Date().toISOString().slice(0,10)}.csv`,
  [
    { label: 'Date', key: 'created_at' },
    { label: 'Artisan', key: 'artisan_name' },
    { label: 'Type', key: 'type' },
    { label: 'Montant (FCFA)', key: 'amount' },
    { label: 'Description', key: 'description' },
    { label: 'Référence', key: 'reference' },
  ]
)

const exportWallets = () => exportCsvColumns(
  wallets.value,
  `portefeuilles_${new Date().toISOString().slice(0,10)}.csv`,
  [
    { label: 'Artisan', key: 'artisan_name' },
    { label: 'Téléphone', key: 'artisan_phone' },
    { label: 'Solde (FCFA)', key: 'balance' },
    { label: 'Revenus totaux (FCFA)', key: 'total_earning' },
    { label: 'Retraits totaux (FCFA)', key: 'total_withdrawal' },
  ]
)

// ── Admin transfer ──
const confirmAdminTransfer = async () => {
  if (!canManageFinance.value) return
  if (!adminTransferPhone.value.trim() || !adminTransferAmount.value) return
  isAdminTransferring.value = true
  try {
    const res = await adminTransfer(adminTransferAmount.value, adminTransferPhone.value.trim(), adminTransferDesc.value || 'Virement admin ProFissa')
    ElMessage.success(`Virement effectué — réf. ${res.reference}`)
    showAdminTransfer.value = false
    adminTransferPhone.value = ''
    adminTransferAmount.value = 0
    adminTransferDesc.value = 'Virement admin ProFissa'
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isAdminTransferring.value = false }
}

onMounted(async () => {
  await loadGlobal()

  if (route.query.tab) {
    activeTab.value = String(route.query.tab)
  }

  if (route.query.tab === 'artisan' && route.query.artisanId) {
    const seededArtisan = {
      id: String(route.query.artisanId),
      full_name: route.query.artisanName ? String(route.query.artisanName) : '',
      phone: route.query.artisanPhone ? String(route.query.artisanPhone) : '',
    }
    artisanSearch.value = seededArtisan.full_name || seededArtisan.phone
    await selectArtisan(seededArtisan)
  }
})
</script>

<style scoped>
:deep(.premium-tabs .el-tabs__item) {
  @apply text-profissa-text-muted font-bold text-[0.95rem] tracking-wide;
}
:deep(.premium-tabs .el-tabs__item.is-active) {
  @apply text-profissa-accent;
}
:deep(.premium-tabs .el-tabs__active-bar) {
  @apply bg-profissa-accent;
}
:deep(.premium-tabs .el-tabs__nav-wrap::after) {
  @apply bg-profissa-glass-border;
}
.bulk-bar { @apply flex items-center gap-3 px-4 py-3 mb-4 rounded-xl; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { @apply font-extrabold text-sm text-profissa-accent mr-auto; }
</style>
